import React, { useMemo, useState } from 'react'
import { useHistory, useLocation, match } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { Col, Row } from 'reactstrap'

import * as productActions from '../../actions/product'
import * as actionsCheckout from '../../actions/checkout'
import { Price, ProductName } from './ProductStyledComponents'
import StoreThemeProvider from '../../components/shared/StoreThemeProvider/StoreThemeProvider'
import Metatags from '../../components/shared/Metatags/Metatags'
import { parse, stringify } from 'query-string'
import { MdMail } from 'react-icons/md'
import CircleIconButton from '../../ui/components/CircleIconButton'
import TopBar from '../../components/layout/TopBar/TopBar'
import dropRight from 'lodash/dropRight'
import ProductShareButton from './ProductShareButton'
import storeService from '../../api/services/storeService'
import config from '../../config'
import Product from 'api/models/Product'
import Store from 'api/models/Store'
import { Notification } from 'react-notification-system'
import StoreError from 'api/StoreError'
import { AxiosResponse } from 'axios'
import { useAutoAddingProduct } from './hooks/useAutoAddingProduct'
import { addProduct } from 'actions/cart'
import Carousel from '../../components/layout/Carousel/Carousel'
import AppContainer from 'components/layout/Container/Container'
import { formatPrice } from 'helpers'
import { ProductCounter } from 'components/shared/ProductCounter/ProductCounter'
import { ButtonWithIcon } from 'components/shared/ButtonWithIcon/ButtonWithIcon'
import Cart from 'api/models/Cart'
import CheckoutModal from 'pages/Store/Checkout/CheckoutModal'
import { omit } from 'lodash'

interface RootState {
  product: Product
  activeStore: Store
  cart: Cart
}

interface StoreServiceParams {
  notify: string
  token?: string
  ct?: string
}

interface Props {
  match: match<{
    productId: string
    storeId: string
  }>
  toggleGeolocationModal: () => void
  changeBackgroundColor: (color: string) => void
  showNotification: (notification: Notification) => void
  setSource: (source: StoreSource) => void
}

export default function ProductPage({
  match,
  changeBackgroundColor,
  toggleGeolocationModal,
  showNotification,
  setSource
}: Props) {
  const dispatch = useDispatch()
  const location = useLocation()
  const intl = useIntl()
  const history = useHistory()
  const { setMessage, AutoAddingLoader, autoAddingEnabled } = useAutoAddingProduct()
  const [productId, setProductId] = React.useState('')
  const [storeId, setStoreId] = React.useState('')
  const [storeSource, setStoreSource] = React.useState('HAUSMART')
  const cart = useSelector((state: RootState) => new Cart(state.cart))
  const activeStore = useSelector((state: RootState) => state.activeStore)
  const [checkoutModalShow, setModalVisible] = useState(false)
  const [storeServiceParams, setStoreServiceParams] = React.useState<StoreServiceParams>({
    notify: 'true',
    token: undefined,
    ct: undefined
  })
  const parsedSearch = useMemo(() => parse(location.search), [location.search])

  React.useEffect(() => {
    changeBackgroundColor(activeStore?.getBackgroundColor())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStore?.shortId])

  React.useEffect(() => {
    match.params.productId && setProductId(match.params.productId)
    match.params.storeId && setStoreId(match.params.storeId.toUpperCase())

    setStoreServiceParams({
      notify: typeof parsedSearch.notify === 'string' ? parsedSearch.notify : 'true',
      ct: typeof parsedSearch.ct === 'string' ? parsedSearch.ct : undefined,
      token: typeof parsedSearch.token === 'string' ? parsedSearch.token : undefined
    })
  }, [match.params, parsedSearch])

  React.useEffect(() => {
    if (activeStore?.getShortId() && productId) {
      productActions
        .fetchProduct(
          storeId,
          productId,
          storeServiceParams.ct
        )(dispatch)
        .then(product => {
          if (autoAddingEnabled) {
            if (product.isSoldOut()) {
              setMessage(' Damn! This product is now sold out. You will be redirected to store.')
            } else {
              setMessage('Adding product to basket. You will be redirected to store.')
              dispatch(addProduct(product))
            }

            setTimeout(() => {
              history.push({ pathname: `/store/${storeId}`, search: stringify(omit(parsedSearch, 'modifier')) })
            }, 4000)
          }
        })
        .catch(e => {
          console.log('🚀 ~ file: Product.tsx ~ line 124 ~ React.useEffect ~ e', e)
          if (autoAddingEnabled) {
            setMessage('Unable to find product. You will be redirected to store')
            setTimeout(() => {
              history.push({ pathname: `/store/${storeId}`, search: stringify(omit(parsedSearch, 'modifier')) })
            }, 3000)
          } else {
            const notification = {
              title: intl.formatMessage({ id: 'general.routeNotFound' }),
              type: 'warning',
              message: 'Error fetching product',
              autoDismiss: 1
            }
            showNotification(notification)
            history.push({ pathname: `/store/${storeId}`, search: stringify(omit(parsedSearch, 'modifier')) })
          }
        })
    }
    // eslint-disable-next-line
  }, [
    storeServiceParams.ct,
    dispatch,
    history,
    intl,
    productId,
    activeStore,
    storeId,
    showNotification,
    autoAddingEnabled,
    setMessage
  ])

  React.useEffect(() => {
    storeService
      .getStore(storeId, {
        notify: storeServiceParams.notify,
        ct: storeServiceParams.ct,
        token: storeServiceParams.token
      })
      .then(store => {
        setSource(store.source)
        setStoreSource(store.source)
      })
      .catch(async (err: StoreError) => {
        const errState = {
          error: {
            message: '',
            type: err.type,
            isNotGeo: err.isNotGeo && err.isNotGeo(),
            isNotFound: err.isNotFound && err.isNotFound()
          },
          isPrivate: err.isPrivate && err.isPrivate(),
          metatags: {},
          storeData: {}
        }

        if (errState.error.isNotGeo) {
          errState.error.message = intl.formatMessage({
            id: 'store.geolocationIssue'
          })
        } else {
          errState.error.message = err.message
        }

        if (errState.isPrivate) {
          const demoStoreId = config.demoShop
          const storeData: AxiosResponse<Store> = await storeService.getStoreMeta(storeId)
          const store = storeData.data
          const sharedImage = store.cover_photo_social
          errState.metatags = {
            og_title: store.name,
            og_description: store.message,
            og_image: sharedImage,
            og_image_secure_url: sharedImage,
            type: 'article',
            noIndex: true
          }
          errState.storeData = store
          await storeService.getStore(demoStoreId, {})
        }
      })
  }, [storeId, intl, storeServiceParams.ct, storeServiceParams.notify, storeServiceParams.token, setSource])

  const backToStore = () => {
    history.push(`/store/${storeId}`)
  }

  const toggleCheckoutModal = () => {
    setModalVisible(!checkoutModalShow)
  }

  const onCheckout = () => {
    dispatch(actionsCheckout.clearCheckout())
    dispatch(actionsCheckout.setCancelIndicator(false))
    dispatch(actionsCheckout.setPaymentStatus('pending', {}, 'checkout', activeStore.getShortId()))
    toggleCheckoutModal()
  }

  const product = useSelector((state: RootState) => state.product)
  const imageId = parse(window.location.search).image_id
  const socialPhoto = typeof imageId === 'string' ? product.getSocialPhoto(imageId) : product.getImage()
  const isZeroTouch = storeSource === 'ZEROTOUCH'
  const metatags = {
    title: `${product.getName()} - ${activeStore.getName()} - ${isZeroTouch ? 'ZeroTouch' : 'HausMart'}`,
    description: product.getDescription(),
    og_title: product.getName(),
    og_description: product.getDescription(),
    og_type: 'og:product',
    og_image: socialPhoto,
    og_image_secure_url: socialPhoto,
    product_price_amount: String(product.getPrice()),
    product_price_currency: 'USD',
    noIndex: !product.isIndexable() || product.isPrivate(),
    richResult: {
      type: 'Product',
      product: product,
      productPath: match.url,
      store: activeStore,
      storePath: match.url && dropRight(match.url.split('/'), 2).join('/')
    }
  }

  return (
    <StoreThemeProvider store={activeStore}>
      <>
        <div
          style={{
            backgroundColor: '#fff',
            paddingBottom: 25,
            marginBottom: 15,
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Metatags {...metatags} />
          <TopBar
            opened={false}
            storeIdSection={false}
            toggleGeolocationModal={toggleGeolocationModal}
            showNotification={showNotification}
            backButton
            backButtonAction={backToStore}
            storeSource={isZeroTouch ? 'ZEROTOUCH' : 'HAUSMART'}
            storeId={activeStore._id}
            ActionButton={() => (
              <CircleIconButton onClick={() => history.push('/hausmart/contact')}>
                <MdMail />
              </CircleIconButton>
            )}
            search={false}
            withLinks={false}
          />

          {autoAddingEnabled ? (
            <AutoAddingLoader />
          ) : (
            <AppContainer>
              <Row className="mt-5">
                <Col sm="7">
                  <Carousel images={product.images}>
                    {product.images.length > 1 && (
                      <>
                        <Carousel.Dots />
                        <Carousel.Thumbnails />
                      </>
                    )}
                  </Carousel>
                </Col>
                <Col sm="5">
                  <ProductName>{product.name}</ProductName>
                  <p className="m-0">{product.description}</p>

                  <Price isWholesale={product.isWholesale()}> {formatPrice(product.getPrice())} </Price>

                  <ProductCounter product={product} />
                  <ButtonWithIcon
                    className="mb-4"
                    text="Procced to checkout"
                    color="#4a7f9f"
                    colorDark="#39627a"
                    fontColor="#fff"
                    disabled={cart.isEmpty()}
                    onClick={onCheckout}
                  >
                    <p style={{ margin: 0, color: '#fff' }}>{formatPrice(cart.getSubtotal(), true)}</p>
                  </ButtonWithIcon>

                  <div className="d-flex" style={{ gap: 20 }}>
                    <ButtonWithIcon
                      icon="home"
                      text="Back to store"
                      color="#000"
                      colorDark="grey"
                      fontColor="#fff"
                      onClick={backToStore}
                    />
                    <ProductShareButton product={product} storeId={storeId}>
                      <ButtonWithIcon icon="share" text="Share" color="#fff" colorDark="grey" fontColor="#000" />
                    </ProductShareButton>
                  </div>
                </Col>
              </Row>
            </AppContainer>
          )}
        </div>
        {checkoutModalShow && (
          <CheckoutModal
            key="checkoutModal"
            storeId={activeStore.getShortId()}
            merchantId={activeStore.getMerchantId()}
            isOpen={checkoutModalShow}
            ct={storeServiceParams.ct}
            toggle={() => {
              toggleCheckoutModal()
            }}
          />
        )}
      </>
    </StoreThemeProvider>
  )
}
